import { Injectable, OnDestroy } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HttpTransportType,
} from '@microsoft/signalr';
import { environment as env } from '@env/environment';

export type TicketCheckInUpdatedHandler = (
  ticketId: number,
  isCheckedIn: boolean
) => void;

@Injectable({
  providedIn: 'root',
})
export class CheckInHubService implements OnDestroy {
  private connection: HubConnection | null = null;
  private currentCampaignId: number | null = null;
  private hubUrl = '';
  /** All subscribers; one internal listener dispatches to all so multiple views get updates. */
  private ticketCheckInHandlers: TicketCheckInUpdatedHandler[] = [];

  constructor() {
    const base = (env.apiUrl ?? '').replace(/\/$/, '');
    this.hubUrl = `${base}/hubs/checkin`;
  }

  /**
   * Connect to the check-in hub and join the campaign group.
   * @param campaignId Campaign to receive check-in updates for
   * @param accessTokenFactory Returns the current JWT (e.g. id token) for auth
   */
  async connect(
    campaignId: number,
    accessTokenFactory: () => Promise<string>
  ): Promise<void> {
    if (
      this.connection?.state === 'Connected' &&
      this.currentCampaignId === campaignId
    ) {
      return;
    }
    await this.disconnect();

    const transport = HttpTransportType.WebSockets;
    this.connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory,
        transport,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    this.connection.onreconnected(async () => {
      if (
        this.currentCampaignId != null &&
        this.connection != null
      ) {
        try {
          await this.connection.invoke('JoinCampaign', this.currentCampaignId);
        } catch (err) {
          console.warn(
            'CheckInHub: failed to rejoin campaign after reconnect',
            { campaignId: this.currentCampaignId },
            err
          );
        }
      }
    });

    this.ensureTicketCheckInListener();

    await this.connection.start();
    await this.connection.invoke('JoinCampaign', campaignId);
    this.currentCampaignId = campaignId;
  }

  /** Event names (server may send PascalCase or camelCase depending on protocol). */
  private static readonly TicketCheckInUpdatedEvents = [
    'ticketCheckInUpdated',
    'TicketCheckInUpdated',
  ] as const;

  /** Normalize server args: server may send (ticketId, isCheckedIn) or single array [ticketId, isCheckedIn]. */
  private static normalizeCheckInArgs(
    ...args: unknown[]
  ): [number, boolean] | null {
    if (
      args.length >= 2 &&
      typeof args[0] === 'number' &&
      typeof args[1] === 'boolean'
    ) {
      return [args[0], args[1]];
    }
    if (args.length === 1 && Array.isArray(args[0]) && args[0].length >= 2) {
      const [a, b] = args[0];
      if (typeof a === 'number' && typeof b === 'boolean') return [a, b];
    }
    return null;
  }

  /**
   * Register one internal listener that dispatches to all subscribed handlers,
   * so multiple check-in manager views (or components) all receive the same event.
   */
  private ensureTicketCheckInListener(): void {
    if (!this.connection) return;
    const dispatch = (...args: unknown[]) => {
      const normalized = CheckInHubService.normalizeCheckInArgs(...args);
      if (!normalized) return;
      const [ticketId, isCheckedIn] = normalized;
      for (const h of this.ticketCheckInHandlers) {
        try {
          h(ticketId, isCheckedIn);
        } catch (err) {
          console.warn('CheckInHub: ticket check-in handler threw', { ticketId, isCheckedIn }, err);
        }
      }
    };
    for (const name of CheckInHubService.TicketCheckInUpdatedEvents) {
      this.connection.off(name);
      this.connection.on(name, dispatch);
    }
  }

  /** Subscribe to server push when a ticket check-in state changes. Multiple subscribers are supported. */
  onTicketCheckInUpdated(handler: TicketCheckInUpdatedHandler): void {
    if (this.ticketCheckInHandlers.indexOf(handler) === -1) {
      this.ticketCheckInHandlers.push(handler);
    }
  }

  /** Unsubscribe a previously added handler (e.g. when component is destroyed). */
  offTicketCheckInUpdated(handler: TicketCheckInUpdatedHandler): void {
    const i = this.ticketCheckInHandlers.indexOf(handler);
    if (i !== -1) this.ticketCheckInHandlers.splice(i, 1);
  }

  /**
   * Disconnect and leave the campaign group.
   */
  async disconnect(): Promise<void> {
    if (!this.connection) return;
    if (this.currentCampaignId != null) {
      try {
        await this.connection.invoke('LeaveCampaign', this.currentCampaignId);
      } catch (err) {
        console.warn(
          'CheckInHub: LeaveCampaign failed on disconnect',
          { campaignId: this.currentCampaignId },
          err
        );
      }
      this.currentCampaignId = null;
    }
    await this.connection.stop();
    this.connection = null;
  }

  isConnected(): boolean {
    return this.connection?.state === 'Connected';
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
